import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarDays, Radio, UserRound } from "lucide-react";
import { Button } from "./ui/button";
import { cohortDayRange, cohortMonth, cohortYear } from "../lib/cohorts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLYER_SRC = "/images/iso9001-lead-auditor-flyer.jpg";

export const UpcomingTrainingSection = () => {
  const [course, setCourse] = useState(null);

  useEffect(() => {
    axios.get(`${API}/courses/upcoming-cohorts`)
      .then((r) => setCourse(r.data.courses?.[0] || null))
      .catch(() => setCourse(null));
  }, []);

  if (!course) return null;
  const courseUrl = `/courses/${course.id}?format=online-live`;
  const trainer = course.cohorts[0];

  return (
    <section className="py-20 bg-white" data-testid="upcoming-training-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Link to={courseUrl} className="lg:col-span-5 group block" data-testid="upcoming-training-flyer-link">
            <img
              src={FLYER_SRC}
              alt={`${course.title} live online training flyer – choose from upcoming course dates with trainer ${trainer.trainer_name || ""}`}
              className="w-full max-w-md mx-auto rounded-2xl shadow-2xl ring-1 ring-slate-200 transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-0.5deg]"
              loading="lazy"
              data-testid="upcoming-training-flyer"
            />
          </Link>

          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 bg-brand/10 text-brand px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-5">
              <Radio className="h-3.5 w-3.5 animate-pulse" /> Upcoming Live Online Training
            </span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-charcoal leading-tight mb-4" data-testid="upcoming-training-title">
              {course.title} Training
            </h2>
            <p className="text-base text-slate-600 mb-8 max-w-xl">
              Lead quality audits with confidence. Five instructor-led days, PECB exam included — for auditors, quality professionals and consultants.
            </p>

            <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal mb-3">Choose your course dates</p>
            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              {course.cohorts.map((c, i) => (
                <Link
                  key={c.id}
                  to={`${courseUrl}&cohort=${c.id}`}
                  className="group rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-brand hover:bg-brand/5 animate-in fade-in slide-in-from-bottom-2"
                  style={{ animationDelay: `${i * 90}ms`, animationFillMode: "both" }}
                  data-testid={`upcoming-cohort-${c.id}`}
                >
                  <CalendarDays className="h-5 w-5 text-brand mb-2" />
                  <p className="text-[11px] font-bold tracking-widest uppercase text-brand">{cohortMonth(c)}</p>
                  <p className="font-heading font-bold text-2xl text-charcoal">{cohortDayRange(c)}</p>
                  <p className="text-sm text-slate-500">{cohortYear(c)}{c.timezone ? ` · ${c.timezone}` : ""}</p>
                </Link>
              ))}
            </div>

            {trainer.trainer_name && (
              <p className="text-sm text-slate-600 mb-8 flex items-center gap-2">
                <UserRound className="h-4 w-4 text-brand" /> Your trainer:{" "}
                <Link to={trainer.trainer_url || "/consultants"} className="font-semibold text-brand hover:underline" data-testid="upcoming-training-trainer-link">
                  {trainer.trainer_name}
                </Link>
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to={courseUrl}>
                <Button size="lg" className="w-full sm:w-auto bg-brand hover:bg-brand-dark text-white" data-testid="upcoming-training-register-btn">
                  Register Now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-brand text-brand hover:bg-brand/5" data-testid="upcoming-training-enquire-btn">
                  Enquire
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
